import React from 'react';
import { useRecentActivity } from '../hooks/useRecentActivity';
import { UserPlus, RefreshCw, GraduationCap, FileEdit } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export function RecentActivity() {
  const { activities, loading, error, refresh } = useRecentActivity();

  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const activityDate = new Date(date);
    const diffInMinutes = Math.floor((now.getTime() - activityDate.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Agora';
    if (diffInMinutes < 60) return `${diffInMinutes} min atrás`;
    if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h atrás`;
    }
    const days = Math.floor(diffInMinutes / 1440);
    return `${days}d atrás`;
  };

  const getEventIcon = (tipo: string) => {
    switch (tipo.toLowerCase()) {
      case 'novo cadastro':
        return <UserPlus className="w-4 h-4 text-emerald-400" />;
      case 'matrícula realizada':
        return <GraduationCap className="w-4 h-4 text-indigo-400" />;
      case 'atualização de cadastro':
      case 'atualização de dados':
        return <FileEdit className="w-4 h-4 text-blue-400" />;
      default:
        return <UserPlus className="w-4 h-4 text-emerald-400" />;
    }
  };

  const getEventColor = (tipo: string) => {
    switch (tipo.toLowerCase()) {
      case 'novo cadastro':
        return 'bg-emerald-500/20';
      case 'matrícula realizada':
        return 'bg-indigo-500/20';
      case 'atualização de cadastro':
      case 'atualização de dados':
        return 'bg-blue-500/20';
      default:
        return 'bg-emerald-500/20';
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, index) => (
          <div key={index} className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <Skeleton className="w-8 h-8 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-red-400 text-sm">Erro ao carregar atividades recentes</p>
          <Button
            onClick={refresh}
            variant="destructive"
            size="sm"
            className="mt-2"
          >
            Tentar novamente
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6 text-center">
          <p className="text-muted-foreground">Nenhuma atividade recente</p>
          <Button
            onClick={refresh}
            variant="link"
            className="mt-2 text-primary"
          >
            Atualizar
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {activities.map((activity, index) => (
        <div key={index} className="flex items-center justify-between py-2">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getEventColor(activity.tipo_do_evento)}`}>
              {getEventIcon(activity.tipo_do_evento)}
            </div>
            <div>
              <p className="text-sm font-medium">
                {activity.nome}
              </p>
              <div className="text-xs text-muted-foreground mt-1">
                {activity.tipo_do_evento}
              </div>
            </div>
          </div>
          <div className="text-right">
            <span className="text-xs text-muted-foreground">
              {formatTimeAgo(activity.created_at)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}